import {
  CustomerSort,
  DEFAULT_PAGE_SIZE,
  listCustomers,
  type CustomerPage,
} from '@/api/customers'
import type { SortDirection } from '@/api/sorting'
import { usePagedList, type PagedList } from '@/hooks/usePagedList'

export type CustomerList = PagedList<CustomerPage, CustomerSort> & {
  includeDeleted: boolean
  setIncludeDeleted: (include: boolean) => void
}

/**
 * The deleted records are an administrator's business: only they can delete or restore one, so a
 * salesperson has nothing to do with a list that shows them. Dropping the key rather than the
 * checkbox alone is what makes that true of a hand-typed `?deleted=true` as well.
 */
const FILTERS = ['deleted'] as const
const WITHOUT_DELETED = [] as const

/** How each column is read, mirroring `CustomerSort` in the backend. */
const DIRECTIONS: Readonly<Record<CustomerSort, SortDirection>> = {
  [CustomerSort.COMPANY_NAME]: 'ASC',
  [CustomerSort.CUSTOMER_NUMBER]: 'ASC',
  [CustomerSort.CITY]: 'ASC',
}

/** @param mayListDeleted whether this user is allowed to see the deleted records at all. */
export function useCustomerList(mayListDeleted: boolean): CustomerList {
  const list = usePagedList<CustomerPage, CustomerSort>({
    filterKeys: mayListDeleted ? FILTERS : WITHOUT_DELETED,
    directions: DIRECTIONS,
    defaultSort: CustomerSort.COMPANY_NAME,
    initialPageSize: DEFAULT_PAGE_SIZE,
    rowCount: (page) => page.customers.length,
    load: ({ search, page, size, sort, direction, filters }, signal) =>
      listCustomers(
        { search, includeDeleted: filters.deleted === 'true', sort, direction, page, size },
        signal,
      ),
  })

  return {
    ...list,
    includeDeleted: list.filters.deleted === 'true',
    setIncludeDeleted: (include) => list.setFilter('deleted', include ? 'true' : ''),
  }
}
